import React, { useEffect, useState } from 'react';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer
} from 'recharts';
import { Box, CircularProgress, Typography } from '@mui/material';
import { theme } from '../theme';
import api from '../services/api';

interface RadarDataPoint {
  category: string;
  value: number;
}

interface RadarChartData {
  initial: RadarDataPoint[];
  latest: RadarDataPoint[];
}

export const CustomRadarChart: React.FC = () => {
  const [data, setData] = useState<RadarChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('[RadarChart] Fetching data');
        const response = await api.get('/api/radar-chart-data/');
        console.log('[RadarChart] Data received:', response.data);
        setData(response.data);
      } catch (err) {
        console.error('Error fetching radar chart data:', err);
        setError('Failed to load progress data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
        <Typography color="error">{error || 'No data available'}</Typography>
      </Box>
    );
  }

  // Only use latest data
  const chartData = data.latest.map(item => ({
    category: item.category,
    value: item.value
  }));

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart 
          cx="50%" 
          cy="50%" 
          outerRadius="80%" 
          data={chartData}
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
        >
          <PolarGrid stroke={theme.palette.primary.light} />
          <PolarAngleAxis
            dataKey="category"
            tick={props => {
              // Custom tick renderer for wrapping long text
              const { x, y, payload, textAnchor } = props;
              const words = String(payload.value).split(' ');
              const maxWordsPerLine = 2;
              const lines = [];
              for (let i = 0; i < words.length; i += maxWordsPerLine) {
                lines.push(words.slice(i, i + maxWordsPerLine).join(' '));
              }
              return (
                <g transform={`translate(${x},${y})`}>
                  {lines.map((line, idx) => (
                    <text
                      key={idx}
                      x={0}
                      y={idx * 14}
                      textAnchor={textAnchor}
                      fill={theme.palette.text.primary}
                      fontSize={12}
                    >
                      {line}
                    </text>
                  ))}
                </g>
              );
            }}
          />
          <PolarRadiusAxis 
            tick={false}
            axisLine={false}
            domain={[0, 7]}
          />
          <Radar
            dataKey="value"
            stroke={theme.palette.primary.main}
            fill={theme.palette.primary.main}
            fillOpacity={0.3}
          />
        </RadarChart>
      </ResponsiveContainer>
    </Box>
  );
};